<script setup>
import { useStorage } from "@vueuse/core";
import { useForm } from "vee-validate";
import { useRouter } from "vue-router";
import * as yup from "yup";

import { ValidateInput, useAuthStore } from "@/entities";
import {
    BaseRow,
    BaseCol,
    BaseButton,
    BaseLoader,
    AUTHENTICATION_ROUTE_NAMES,
} from "@/shared";

const router = useRouter();

const authenticationStore = useAuthStore();
const { authUser } = authenticationStore;

const validationSchema = yup.object({
    login: yup.string().min(2).required(),
    password: yup.string().min(2).required(),
});

const { isSubmitting, errors, handleSubmit } = useForm({
    validationSchema,
});

const handleLogin = handleSubmit(async (values, { resetForm, setErrors }) => {
    try {
        resetForm();
        await authUser(values);

        useStorage("just-login", true, sessionStorage);
    } catch (e) {
        setErrors({
            invalid: "Неверный логин или пароль",
        });
    }
});

const apiURL = import.meta.env.VITE_APP_API;
</script>

<template>
    <div class="login">
        <h1 class="login__heading">Войти</h1>
        <form class="login__form" @keypress.enter="handleLogin">
            <ValidateInput
                id="input-login"
                class="login__input"
                size="lg"
                name="login"
                type="text"
                label="Логин"
                placeholder="Введите логин или почту..."
                required
            />
            <ValidateInput
                id="input-password"
                class="login__input login__input--margin"
                size="lg"
                name="password"
                type="password"
                label="Пароль"
                placeholder="Введите пароль..."
                required
            >
                <template #additional>
                    <div class="login__forgot-wrapper">
                        <RouterLink
                            :to="{
                                name: AUTHENTICATION_ROUTE_NAMES.RESTORE_ENTER_MAIL,
                            }"
                            class="login__forgot"
                        >
                            Забыл пароль
                        </RouterLink>
                    </div>
                </template>
            </ValidateInput>
            <BaseRow class="login__button-wrapper">
                <BaseCol cols="6">
                    <BaseButton
                        class="login__button"
                        variant="gray"
                        size="lg"
                        @click="
                            router.push({
                                name: AUTHENTICATION_ROUTE_NAMES.REGISTRATION,
                            })
                        "
                    >
                        Регистрация
                    </BaseButton>
                </BaseCol>
                <BaseCol cols="6">
                    <BaseButton
                        class="login__button"
                        variant="secondary"
                        size="lg"
                        @click="handleLogin"
                    >
                        <BaseLoader
                            v-if="isSubmitting"
                            class="login__submitting"
                            size="xs"
                            background-color="var(--c-secondary)"
                            :fillParent="false"
                        />
                        Войти
                    </BaseButton>
                </BaseCol>
            </BaseRow>
            <div v-if="errors.invalid" class="login__error">
                {{ errors.invalid }}
            </div>
            <div class="social-auth">
                <p class="social-auth__heading">Войти c помощью</p>
                <div class="social-auth__links">
                    <a :href="apiURL + '/auth/vk'" class="social-auth__link">
                        <img width="35" src="@/shared/images/vk.svg" alt="VK" />
                    </a>
                    <a
                        :href="apiURL + '/auth/google'"
                        class="social-auth__link"
                    >
                        <img
                            width="35"
                            src="@/shared/images/google_logo.svg"
                            alt="Google"
                        />
                    </a>
                </div>
            </div>
        </form>
    </div>
</template>

<style lang="scss" scoped>
.login {
    display: flex;

    flex-direction: column;

    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
        margin-top: 50px;
    }

    &__heading {
        margin-bottom: 70px;

        font-size: var(--default-h1-size);
        font-weight: 500;
        line-height: var(--default-line-height);

        color: var(--c-heading);

        @media (max-width: 599px) {
            margin-bottom: 30px;

            font-size: 30px;
        }

        @media (max-width: 350px) {
            margin-bottom: 20px;
        }
    }

    &__form {
        width: 100%;
    }

    &__input {
        margin-bottom: 30px;

        &--margin {
            margin-bottom: 50px;
        }

        @media (max-width: 599px) {
            margin-bottom: 20px;

            &--margin {
                margin-bottom: 30px;
            }
        }

        @media (max-width: 599px) {
            margin-bottom: 15px;

            &--margin {
                margin-bottom: 20px;
            }
        }
    }

    &__forgot-wrapper {
        text-align: right;
    }

    &__forgot {
        display: inline-block;

        margin-top: 5px;

        font-size: 16px;
        font-weight: 400;
        color: #858585;
        text-decoration: underline;

        &:hover {
            color: var(--c-primary);
            text-decoration: none;
        }

        @media (max-width: 599px) {
            font-size: 12px;
        }
    }

    @media (max-height: 620px) {
        padding-bottom: 1rem;
    }

    &__button-wrapper {
        margin-bottom: 10px;
    }

    &__button {
        width: 100%;
    }

    &__submitting {
        margin-right: 10px;
    }

    &__error {
        width: 100%;

        font-size: var(--base-form-input-text-size-error);
        color: var(--input-color-error);
        text-align: center;
    }

    .social-auth {
        font-size: var(--default-text-size);
        text-align: center;

        &__heading {
            margin: 20px 0 10px;
        }

        &__link {
            display: inline-block;

            width: 40px;
            height: 40px;
        }

        &__links {
            display: flex;

            gap: 10px;

            align-items: center;
            justify-content: center;
        }
    }
}
</style>
